import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useMemberAuth } from '../../context/MemberAuthContext';
import { memberAuthAPI, dhikrAPI, sawiaAPI, directoryAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { DASHBOARD_SECTIONS, ROLE_INFO, canAccess } from '../../utils/roles';
import { RoleBadge, RoleGate, PendingRoleCard, RoleProgressPath } from '../../components/member/RoleComponents';
import {
  HomeIcon, UserIcon, BookOpenIcon,
  UsersIcon, ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

const ALL_NAV = [
  { section: 'profile',   to: '/member/dashboard/profile',    label: 'My Profile',   icon: UserIcon    },
  { section: 'dhikr',     to: '/member/dashboard/dhikr',      label: 'Dhikr Counter',icon: BookOpenIcon },
  { section: 'directory', to: '/member/dashboard/directory',  label: 'Directory',    icon: UsersIcon   },
];

// ── Member Layout ─────────────────────────────────────────────
export function MemberLayout() {
  const { member, logout } = useMemberAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/member/login'); };

  const allowedSections = DASHBOARD_SECTIONS[member?.member_role] || ['profile'];
  const visibleNav = ALL_NAV.filter(n => allowedSections.includes(n.section));

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-gradient-to-b from-navy-800 to-navy-900">
      {/* Header */}
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-11 w-11 rounded-full border-2 border-cyan-400 overflow-hidden flex-shrink-0 bg-navy-700">
            {member?.profile_photo
              ? <img src={member.profile_photo} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-lg text-white/60">👤</div>
            }
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{member?.full_name}</p>
            <RoleBadge role={member?.member_role} />
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <NavLink to="/member/dashboard" end onClick={() => setOpen(false)}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive ? 'bg-cyan-400 text-navy-900' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
          <HomeIcon className="h-5 w-5 flex-shrink-0" />Dashboard
        </NavLink>
        {visibleNav.map(({ to, label, icon: Icon, section }) => (
          <NavLink key={to} to={to} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive ? 'bg-cyan-400 text-navy-900' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
            <Icon className="h-5 w-5 flex-shrink-0" />{label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <Link to="/" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/40 hover:text-white hover:bg-white/10 text-xs transition-all">
          ← Public Site
        </Link>
        <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 w-full text-left text-sm transition-all">
          <ArrowLeftStartOnRectangleIcon className="h-5 w-5" />Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <div className="hidden lg:flex lg:w-60 flex-shrink-0"><div className="flex flex-col w-full"><Sidebar /></div></div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 z-50"><Sidebar /></div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 bg-navy-900 border-b border-white/10">
          <button onClick={() => setOpen(true)} className="text-white p-1"><Bars3Icon className="h-6 w-6" /></button>
          <p className="text-white font-semibold text-sm">Member Portal</p>
        </div>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6"><Outlet /></main>
      </div>
    </div>
  );
}

// ── Dashboard Home ────────────────────────────────────────────
export function MemberDashboardHome() {
  const { member } = useMemberAuth();
  const isPending = member?.member_role === 'pending';

  const profileFields = [
    member?.date_of_birth, member?.gender, member?.marital_status,
    member?.home_address, member?.home_town, member?.education_level, member?.employment_status,
  ];
  const completedFields = profileFields.filter(Boolean).length;
  const profilePct = Math.round((completedFields / profileFields.length) * 100);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-navy-800">
          As-salamu alaykum, {member?.full_name?.split(' ')[0]}! 🌙
        </h1>
        <p className="text-gray-400 text-sm mt-0.5">Welcome to your JNF member portal</p>
      </div>

      {isPending && <PendingRoleCard />}

      {/* Role journey */}
      {!isPending && <div className="mb-5"><RoleProgressPath currentRole={member?.member_role} /></div>}

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {/* Profile completion */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-navy-700 text-sm">Profile Completion</h3>
            <span className="text-cyan-600 font-bold text-lg">{profilePct}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full mb-3">
            <div className="h-full rounded-full bg-gradient-to-r from-navy-600 to-cyan-400 transition-all" style={{ width: `${profilePct}%` }} />
          </div>
          <p className="text-gray-400 text-xs mb-3">
            {profilePct < 100 ? 'A complete profile helps admins assign the right role for you.' : 'Your profile is complete! Baarakallahu feek.'}
          </p>
          {profilePct < 100 && <Link to="/member/dashboard/profile" className="text-xs font-semibold text-cyan-600 hover:underline">Complete profile →</Link>}
        </div>

        {/* Membership card */}
        <div className="bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl p-5 text-white">
          <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-3">Membership</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-white/60">Role</span>
              <RoleBadge role={member?.member_role} />
            </div>
            {member?.sawia_name && (
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Sawia</span>
                <span className="font-medium">{member.sawia_name}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-white/60">Joined</span>
              <span className="font-medium">{member?.created_at ? new Date(member.created_at).getFullYear() : '—'}</span>
            </div>
            {member?.role_assigned_at && (
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Role assigned</span>
                <span className="font-medium">{new Date(member.role_assigned_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick access tiles — filtered by role */}
      {!isPending && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { section:'dhikr',     to:'/member/dashboard/dhikr',     icon:'📿', label:'Dhikr Counter' },
            { section:'directory', to:'/member/dashboard/directory', icon:'👥', label:'Directory' },
            { section:'profile',   to:'/member/dashboard/profile',   icon:'👤', label:'My Profile' },
          ].filter(t => (DASHBOARD_SECTIONS[member?.member_role]||[]).includes(t.section)).map(({ to, icon, label }) => (
            <Link key={to} to={to} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-center hover:shadow-md hover:-translate-y-0.5 transition-all group">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">{icon}</div>
              <p className="font-semibold text-navy-800 text-sm group-hover:text-cyan-600 transition-colors">{label}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Profile Page ──────────────────────────────────────────────
export function MemberProfilePage() {
  const { member, refreshMember } = useMemberAuth();
  const [form, setForm]   = useState({});
  const [sawias, setSawias] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (member) setForm({ ...member });
    sawiaAPI.getAll().then(r => setSawias(r.data.data || []));
  }, [member]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await memberAuthAPI.updateProfile(form);
      await refreshMember();
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update.'); }
    finally { setSaving(false); }
  };

  const F = ({ label, children }) => <div><label className="form-label">{label}</label>{children}</div>;
  const Sel = ({ k, opts }) => (
    <select className="form-input" value={form[k]||''} onChange={e => set(k, e.target.value)}>
      <option value="">Select...</option>
      {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  return (
    <div>
      <h1 className="text-xl font-bold text-navy-800 mb-6">My Profile</h1>
      <form onSubmit={handleSave} className="space-y-5 max-w-3xl">

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-navy-700 pb-2 border-b border-gray-100">Personal</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Phone"><input className="form-input" value={form.phone||''} onChange={e=>set('phone',e.target.value)}/></F>
            <F label="Date of Birth"><input type="date" className="form-input" value={form.date_of_birth?.split('T')[0]||''} onChange={e=>set('date_of_birth',e.target.value)}/></F>
            <F label="Gender"><Sel k="gender" opts={[['male','Male'],['female','Female']]}/></F>
            <F label="Marital Status"><Sel k="marital_status" opts={[['single','Single'],['married','Married'],['divorced','Divorced'],['widowed','Widowed']]}/></F>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-navy-700 pb-2 border-b border-gray-100">Address</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><F label="Home Address"><textarea rows={2} className="form-input resize-none" value={form.home_address||''} onChange={e=>set('home_address',e.target.value)}/></F></div>
            <F label="Home Town"><input className="form-input" value={form.home_town||''} onChange={e=>set('home_town',e.target.value)}/></F>
            <F label="State of Origin"><input className="form-input" value={form.state_of_origin||''} onChange={e=>set('state_of_origin',e.target.value)}/></F>
            <F label="Country"><input className="form-input" value={form.country||''} onChange={e=>set('country',e.target.value)}/></F>
            <F label="Sawia Branch">
              <select className="form-input" value={form.sawia_id||''} onChange={e=>set('sawia_id',e.target.value)}>
                <option value="">— Select Sawia —</option>
                {sawias.map(s=><option key={s.id} value={s.id}>{s.name} ({s.city})</option>)}
              </select>
            </F>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-navy-700 pb-2 border-b border-gray-100">Education</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <F label="Level"><Sel k="education_level" opts={[['primary','Primary'],['secondary','Secondary'],['diploma','Diploma'],['bachelor',"Bachelor's"],['master',"Master's"],['phd','PhD'],['other','Other']]}/></F>
            <F label="Institution"><input className="form-input" value={form.institution||''} onChange={e=>set('institution',e.target.value)}/></F>
            <F label="Field of Study"><input className="form-input" value={form.field_of_study||''} onChange={e=>set('field_of_study',e.target.value)}/></F>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-navy-700 pb-2 border-b border-gray-100">Employment</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <F label="Status"><Sel k="employment_status" opts={[['employed','Employed'],['self_employed','Self-Employed'],['unemployed','Unemployed'],['student','Student'],['retired','Retired']]}/></F>
            <F label="Employer"><input className="form-input" value={form.employer_name||''} onChange={e=>set('employer_name',e.target.value)}/></F>
            <F label="Occupation"><input className="form-input" value={form.occupation||''} onChange={e=>set('occupation',e.target.value)}/></F>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h2 className="font-semibold text-navy-700 pb-2 border-b border-gray-100">Tijaniyyah</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Date Initiated"><input type="date" className="form-input" value={form.date_initiated?.split('T')[0]||''} onChange={e=>set('date_initiated',e.target.value)}/></F>
            <F label="Initiated By (Muqaddam)"><input className="form-input" value={form.initiated_by||''} onChange={e=>set('initiated_by',e.target.value)}/></F>
          </div>
        </section>

        <button type="submit" disabled={saving} className="btn-primary py-3 px-8 flex items-center gap-2">
          {saving ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Saving...</> : 'Save Profile'}
        </button>
      </form>
    </div>
  );
}

// ── Dhikr (requires general+) ─────────────────────────────────
export function DhikrPage() {
  const { member } = useMemberAuth();
  const [types, setTypes]     = useState([]);
  const [selected, setSelected] = useState(null);
  const [count, setCount]     = useState(0);
  const [target, setTarget]   = useState(33);
  const [history, setHistory] = useState([]);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    dhikrAPI.getTypes().then(r => { setTypes(r.data.data||[]); setSelected(r.data.data?.[0]); });
  }, []);
  useEffect(() => {
    if (canAccess(member?.member_role, 'general')) {
      dhikrAPI.getHistory().then(r => setHistory(r.data.data||[]));
      dhikrAPI.getToday().then(r => { const t=r.data.data||[]; if(t.length){setCount(t[0].count);setTarget(t[0].target||33);} });
    }
  }, [member]);
  useEffect(() => { if(selected){setTarget(selected.default_target||33);setCount(0);setSaved(false);} }, [selected]);

  const tap = () => { setCount(c=>c+1); navigator.vibrate&&navigator.vibrate(25); setSaved(false); };
  const save = async () => {
    if(!selected) return;
    await dhikrAPI.save({ dhikr_type: selected.key, count, target });
    toast.success('Saved!'); setSaved(true);
    dhikrAPI.getHistory().then(r => setHistory(r.data.data||[]));
  };
  const pct = target ? Math.min(100,(count/target)*100) : 0;
  const C = 2*Math.PI*90;

  return (
    <div>
      <h1 className="text-xl font-bold text-navy-800 mb-5">📿 Dhikr Counter</h1>
      <RoleGate member={member} accessLevel="general" lockMessage="The Dhikr counter is available to General Members and above.">
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
              {types.map((t,i) => {
                const colors=['#1a1a6e','#1eb5e8','#22c55e','#f59e0b','#8b5cf6','#ef4444','#ec4899'];
                return (
                  <button key={t.key} onClick={()=>setSelected(t)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${selected?.key===t.key?'text-white border-transparent':'border-gray-200 text-gray-600'}`}
                    style={selected?.key===t.key?{background:colors[i%colors.length]}:{}}>
                    {t.key}
                  </button>
                );
              })}
            </div>
            {selected && (
              <div className="text-center">
                <p className="arabic text-2xl text-navy-800 mb-1">{selected.ar}</p>
                <div className="relative inline-flex items-center justify-center my-4">
                  <svg width="210" height="210" viewBox="0 0 210 210" className="rotate-[-90deg]">
                    <circle cx="105" cy="105" r="90" fill="none" stroke="#f0f2f8" strokeWidth="10"/>
                    <circle cx="105" cy="105" r="90" fill="none" stroke="#1eb5e8" strokeWidth="10"
                      strokeDasharray={C} strokeDashoffset={C-(C*pct)/100} strokeLinecap="round"
                      style={{transition:'stroke-dashoffset 0.3s ease'}}/>
                  </svg>
                  <div className="absolute text-center">
                    <p className="text-4xl font-bold text-navy-800">{count}</p>
                    <p className="text-gray-400 text-sm">/ {target}</p>
                  </div>
                </div>
                {pct>=100 && <div className="bg-green-50 border border-green-200 rounded-2xl p-2.5 mb-3 text-green-700 text-sm font-medium">🎉 Alhamdulillah! Target reached!</div>}
                <button onClick={tap} className="h-20 w-20 mx-auto rounded-full bg-navy-700 hover:bg-navy-800 active:scale-95 text-white text-2xl shadow-xl flex items-center justify-center select-none mb-4" style={{touchAction:'manipulation'}}>📿</button>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div><label className="text-xs text-gray-400 block mb-1">Target</label><input type="number" min={1} className="form-input text-center w-20 py-1.5 text-sm" value={target} onChange={e=>setTarget(parseInt(e.target.value)||33)}/></div>
                </div>
                <div className="flex justify-center gap-3">
                  <button onClick={()=>{setCount(0);setSaved(false);}} className="btn-outline py-2 px-4 text-sm">Reset</button>
                  <button onClick={save} disabled={saved} className="btn-primary py-2 px-4 text-sm">{saved?'✓ Saved':'Save'}</button>
                </div>
              </div>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-semibold text-navy-700 mb-3 text-sm">History</h3>
            {history.length===0 ? <p className="text-gray-400 text-sm text-center py-6">No sessions yet.</p> : (
              <div className="space-y-2">
                {history.map(h=>(
                  <div key={h.dhikr_type} className="flex justify-between p-2.5 bg-gray-50 rounded-xl">
                    <div><p className="text-sm font-medium text-navy-800">{h.dhikr_type}</p><p className="text-xs text-gray-400">{h.sessions} session{h.sessions>1?'s':''}</p></div>
                    <div className="text-right"><p className="text-lg font-bold text-cyan-600">{h.total_count?.toLocaleString()}</p></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </RoleGate>
    </div>
  );
}

// ── Member Directory ──────────────────────────────────────────
export function MemberDirectoryPage() {
  const { member } = useMemberAuth();
  const [members, setMembers] = useState([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = () => { setLoading(true); directoryAPI.getAll({ search }).then(r=>setMembers(r.data.data||[])).finally(()=>setLoading(false)); };
  useEffect(()=>{fetch();},[]);

  return (
    <div>
      <h1 className="text-xl font-bold text-navy-800 mb-2">👥 Member Directory</h1>
      <RoleGate member={member} accessLevel="full" lockMessage="The member directory is available to Fully Members and Muqaddams.">
        <div className="flex gap-3 mb-5">
          <input className="form-input flex-1 text-sm py-2.5" placeholder="Search name, town, occupation..." value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetch()}/>
          <button onClick={fetch} className="btn-primary py-2.5 px-5 text-sm">Search</button>
        </div>
        {loading ? <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(8)].map((_,i)=><div key={i} className="h-44 bg-gray-100 rounded-2xl animate-pulse"/>)}</div> : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {members.map(m=>(
              <div key={m.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center hover:shadow-md transition-shadow">
                <div className="h-12 w-12 mx-auto rounded-full border-2 border-cyan-100 overflow-hidden mb-2 bg-navy-50">
                  {m.profile_photo ? <img src={m.profile_photo} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-lg">👤</div>}
                </div>
                <p className="font-semibold text-navy-800 text-sm line-clamp-1 mb-1">{m.full_name}</p>
                <RoleBadge role={m.member_role}/>
                {m.home_town && <p className="text-gray-400 text-xs mt-1.5">📍 {m.home_town}</p>}
                {m.occupation && <p className="text-gray-400 text-xs">{m.occupation}</p>}
                {m.sawia_name && <p className="text-cyan-500 text-xs mt-1 font-medium">{m.sawia_name}</p>}
              </div>
            ))}
            {members.length===0 && <p className="col-span-full text-center text-gray-400 py-10">No members found.</p>}
          </div>
        )}
      </RoleGate>
    </div>
  );
}

export { DhikrPage as default };
